import { useEffect, useState } from 'react';
import { BallTriangle } from 'react-loader-spinner';

/* Universal search for Mark Placement Ready: find any student in the branch
   by name / email / phone and mark them ready without picking course & batch. */
const PlacementReadySearch = ({
  searchStudents,
  results,
  setResults,
  confirmPlacementReady,
}) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  /* ================= DEBOUNCED SEARCH ================= */
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchStudents(term);
        if (cancelled) return;
        setResults(
          (res?.data?.data || []).map((s) => ({
            id: s._id,
            name: s.name,
            email: s.email,
            contactNumber: s.contactNumber,
            courseId: s.courseId,
            courseName: s.courseName,
            batchId: s.batchId,
            batchName: s.batchName,
            branchId: s.branchId,
            branchName: s.branchName,
            placementPriority: s.placementPriority || 'Medium',
            ready: s.readyToPlace || false,
          }))
        );
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  /* ================= PRIORITY CHANGE ================= */
  const handlePriorityChange = (row, value) => {
    setResults((prev) =>
      prev.map((s) =>
        s.id === row.id && s.batchId === row.batchId
          ? { ...s, placementPriority: value }
          : s
      )
    );
  };

  const searching = query.trim().length >= 2;

  return (
    <div className="w-full px-3 pt-7 md:px-8">
      <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
        Search any student
      </label>
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search by name, email or phone..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-3 text-gray-400"
          >
            ✕
          </button>
        )}
      </div>

      {searching &&
        (loading ? (
          <div className="flex h-32 items-center justify-center">
            <BallTriangle color="#5431ed" height={60} width={60} />
          </div>
        ) : results.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No students found for "{query.trim()}"
          </p>
        ) : (
          <div className="mt-4 max-h-[420px] overflow-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  {[
                    'NAME',
                    'COURSE / BATCH',
                    'PRIORITY',
                    'STATUS',
                    'ACTION',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-center text-xs font-bold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((student) => (
                  <tr
                    key={`${student.id}-${student.batchId}`}
                    className={`border-b ${
                      student.ready ? 'bg-green-50 dark:bg-green-900/10' : ''
                    }`}
                  >
                    <td className="py-3 text-center">
                      <div className="font-medium dark:text-white">
                        {student.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {student.email || student.contactNumber}
                      </div>
                    </td>
                    <td className="py-3 text-center text-sm dark:text-white">
                      <div>{student.courseName}</div>
                      <div className="text-xs text-gray-500">
                        {student.batchName}
                        {student.branchName && ` · ${student.branchName}`}
                      </div>
                    </td>
                    <td className="py-3 text-center">
                      <select
                        value={student.placementPriority}
                        onChange={(e) =>
                          handlePriorityChange(student, e.target.value)
                        }
                        disabled={student.ready}
                        className="dark:bg-navy-800"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </td>
                    <td className="py-3 text-center">
                      {student.ready ? (
                        <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-700">
                          Ready
                        </span>
                      ) : (
                        <span className="rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-700">
                          Not Ready
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      <button
                        onClick={() => confirmPlacementReady(student)}
                        disabled={student.ready}
                        className={`rounded px-3 py-1 text-xs ${
                          student.ready
                            ? 'cursor-not-allowed bg-gray-300'
                            : 'bg-brand-500 text-white hover:bg-brand-600'
                        }`}
                      >
                        {student.ready ? 'Already Ready' : 'Mark Ready'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      <div className="my-6 flex items-center gap-3 text-xs text-gray-400">
        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
        OR SELECT COURSE &amp; BATCH
        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
      </div>
    </div>
  );
};

export default PlacementReadySearch;
